import {
    useEffect, useMemo, useRef, useContext,
    useCallback, useState,
} from 'react';
import { Badge } from 'reactstrap';
import cls from 'classnames';
import {
    Map, AdvancedMarker, useMap,
} from '@vis.gl/react-google-maps';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../Common';
import { GoogleSearch } from '../../../common/Map/GoogleSearch';
import { FormContext, IconButton } from '../../../common/forms/FormElements';
import { useJobQuery } from '../common/useJobQuery';
import { MAX_ZOOM, useClusters } from '../../../common/Map/useClusters';
import { InfoPane } from './InfoPane';
import { EmployeeMarkers } from './EmployeeMarkers';
import { EmployeeIndicatorLegend } from './common';

const DEFAULT_ZOOM = 11;
const DEFAULT_US_CENTER = { lat: 39.8283, lng: -98.5795 };

export const MapView = ({ mapId }) => {
    const { setFieldValue, values } = useContext(FormContext);
    const { jobId, dispatchOfficeLocationId } = values;

    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const [legendOpen, setLegendOpen] = useState(true);

    const { data: jobs = [] } = useJobQuery();
    const map = useMap();

    const lastDispatchCenterRef = useRef(null);
    const [searchCenter, setSearchCenter] = useState(null);

    const { data: dispatchLocations } = useQuery({
        queryKey: ['dispatchOffices'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
    });

    const [clusters, supercluster] = useClusters(jobs, bounds, zoom);

    const handlePlaceSelect = useCallback((place) => {
        if (!place) {
            setSearchCenter(null);
            return;
        }

        const loc = place.location;
        if (!loc) {
            return;
        }

        const newCenter = { lat: loc.lat(), lng: loc.lng(), place };
        setSearchCenter(newCenter);

        if (map) {
            map.panTo({ lat: newCenter.lat, lng: newCenter.lng });
            map.setZoom(DEFAULT_ZOOM);
        }
    }, [map]);

    const handleClusterClick = useCallback((latLng, clusterId) => {
        const expansionZoom = Math.min(
            supercluster.getClusterExpansionZoom(clusterId),
            MAX_ZOOM,
        );

        map.setZoom(expansionZoom);
        map.setCenter(latLng);
    }, [supercluster, map]);

    const dispatchCenter = useMemo(() => {
        const location = dispatchLocations.find((loc) => loc.value === dispatchOfficeLocationId);
        const lat = location?.latitude ?? location?.Latitude ?? null;
        const lng = location?.longitude ?? location?.Longitude ?? null;

        if (lat == null || lng == null) {
            return null;
        };

        const latNum = Number(lat);
        const lngNum = Number(lng);

        if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
            return null;
        };

        return { lat: latNum, lng: lngNum };
    }, [dispatchLocations, dispatchOfficeLocationId]);

    useEffect(() => {
        if (!map || !dispatchCenter) {
            return;
        }

        const isSameCenter = lastDispatchCenterRef.current
            && lastDispatchCenterRef.current.lat === dispatchCenter.lat
            && lastDispatchCenterRef.current.lng === dispatchCenter.lng;

        if (isSameCenter) {
            return;
        }

        lastDispatchCenterRef.current = dispatchCenter;

        map.panTo(dispatchCenter);
        map.setZoom(DEFAULT_ZOOM);
    }, [map, dispatchCenter]);

    return (
        <>
            <div className="map-left position-relative">
                <Map
                    mapId={mapId}
                    defaultCenter={dispatchCenter || DEFAULT_US_CENTER}
                    defaultZoom={!isEmpty(jobs) ? DEFAULT_ZOOM : 4}
                    gestureHandling="greedy"
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    zoomControl={false}
                    scaleControl={false}
                    clickableIcons={false}
                    cameraControl={false}
                    onBoundsChanged={(e) => {
                        setBounds(e.detail.bounds);
                    }}
                    onZoomChanged={(e) => {
                        setZoom(e.detail.zoom);
                    }}
                >
                    <GoogleSearch
                        onPlaceSelect={handlePlaceSelect}
                        selectedPlace={searchCenter?.place}
                    />

                    {clusters.map((cluster) => {
                        const [lng, lat] = cluster.geometry.coordinates;
                        const {
                            cluster: isCluster,
                            sum,
                        } = cluster.properties;

                        if (isCluster) {
                            return (
                                <AdvancedMarker
                                    key={`cluster-${cluster.id}`}
                                    position={{ lat, lng }}
                                    title={`${sum} jobs at this location`}
                                    className="cluster-marker"
                                >
                                    <IconButton
                                        onClick={() => handleClusterClick({ lat, lng }, cluster.id)}
                                    >
                                        {sum}
                                    </IconButton>
                                </AdvancedMarker>
                            );
                        }

                        const { jobs, objectID: key } = cluster.properties;

                        if (jobs?.length > 1) {
                            const hasSelectedJob = jobs.some((job) => job.jobId === jobId);

                            return (
                                <AdvancedMarker
                                    key={`cluster-jobs-${key}`}
                                    position={{ lat, lng }}
                                    title={`${jobs.length} jobs at this location`}
                                    onClick={() => {
                                        setFieldValue('jobGroup', jobs);
                                        setFieldValue('employeeGroup', null);
                                    }}
                                    className="marker-group"
                                >
                                    <div>
                                        <FontAwesomeIcon
                                            icon={faMapMarkerAlt}
                                            size="2x"
                                            className={cls('map-marker', 'text-danger', hasSelectedJob && 'map-marker-selected')}
                                        />

                                        <Badge
                                            color="danger"
                                            className="shadow border"
                                            pill
                                        >
                                            {jobs.length}
                                        </Badge>
                                    </div>
                                </AdvancedMarker>
                            );
                        }

                        const [job] = jobs;

                        return (
                            <AdvancedMarker
                                key={`job-${key}`}
                                position={{ lat, lng }}
                                title={`${job.customer || ''} - ${job.jobNumber}`}
                                onClick={() => {
                                    setFieldValue('jobGroup', jobs);
                                    setFieldValue('employeeGroup', null);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    size="2x"
                                    className={cls('map-marker', 'text-danger', jobId === job.jobId && 'map-marker-selected')}
                                />
                            </AdvancedMarker>
                        );
                    })}

                    <EmployeeMarkers dispatchLocations={dispatchLocations} />
                </Map>

                <div
                    className="bg-white rounded p-2 map-legend shadow"
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="text-uppercase small text-muted">
                            Employee Indicators
                        </div>
                        <button
                            type="button"
                            className="btn btn-link p-0 small"
                            onClick={() => setLegendOpen((open) => !open)}
                        >
                            {legendOpen ? 'Hide' : 'Show'}
                        </button>
                    </div>

                    {legendOpen && (
                        <div className="d-flex flex-column mt-2">
                            {EmployeeIndicatorLegend.map((item) => (
                                <div
                                    key={item.label}
                                    className="d-flex align-items-center mb-1"
                                >
                                    <FontAwesomeIcon
                                        icon={item.icon}
                                        style={{ color: item.color, fontSize: 12 }}
                                        className="mr-2"
                                    />
                                    <span className="small">{item.title}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <InfoPane />
        </>
    );
};
